import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { formatDate, formatTime, formatDuration } from '../../utils/dateUtils';

// Safari-safe: formatToParts guarantees individual components regardless of locale quirks
function dateToCT(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

function getTodayCT() {
  return dateToCT(new Date());
}

function getLast7DaysCT() {
  return dateToCT(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
}

function getThisMonthStartCT() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(now);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  return `${y}-${m}-01`;
}

export default function CallHistoryPage() {
  const navigate = useNavigate();
  const [activePreset, setActivePreset] = useState('today');
  const [filters, setFilters] = useState({
    property_name: '',
    date_from: getTodayCT(),
    date_to: getTodayCT(),
    region: '',
    resolution_type: '',
    created_by: '',
    resolved_by: '',
  });
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('');

  function handleSort(col) {
    if (sortBy !== col) { setSortBy(col); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else { setSortBy(''); setSortDir(''); }
  }
  function sortArrow(col) { if (sortBy !== col) return ''; return sortDir === 'asc' ? ' \u25B2' : ' \u25BC'; }

  const thStyle = { cursor: 'pointer', userSelect: 'none' };

  // Load active users for filter dropdowns
  useEffect(() => {
    api.getActiveUsers().then(setActiveUsers).catch(() => {});
  }, []);

  const buildFilters = useCallback(() => {
    const params = {};
    if (filters.property_name) params.property_name = filters.property_name;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.region) params.region = filters.region;
    if (filters.resolution_type) params.resolution_type = filters.resolution_type;
    if (filters.created_by) params.created_by = filters.created_by;
    if (filters.resolved_by) params.resolved_by = filters.resolved_by;
    return params;
  }, [filters]);

  const doSearch = useCallback(async (pageNum, searchFilters, sb, sd) => {
    setLoading(true);
    setError('');
    try {
      const queryFilters = { ...searchFilters, page: pageNum, page_size: 50 };
      if (sb) { queryFilters.sort_by = sb; queryFilters.sort_dir = sd; }
      const data = await api.getCallHistory(queryFilters);
      if (data && data.pagination) {
        setResults(data.data);
        setPagination(data.pagination);
        setPage(data.pagination.page);
      } else {
        setResults(Array.isArray(data) ? data : []);
        setPagination(null);
      }
      setSearched(true);
      // Load stats
      api.getCallHistoryStats(searchFilters).then(setStats).catch(() => {});
    } catch (err) {
      setError(err.message || 'Search failed');
    }
    setLoading(false);
  }, []);

  // Auto-search on page load (Today preset)
  useEffect(() => {
    doSearch(1, { date_from: getTodayCT(), date_to: getTodayCT() }, '', '');
  }, []); // eslint-disable-line

  // Re-search when sort changes
  useEffect(() => {
    if (searched) {
      doSearch(1, buildFilters(), sortBy, sortDir);
    }
  }, [sortBy, sortDir]); // eslint-disable-line

  function handlePreset(preset) {
    setActivePreset(preset);
    if (preset === 'today') {
      const today = getTodayCT();
      const newFilters = { ...filters, date_from: today, date_to: today };
      setFilters(newFilters);
      setPage(1);
      doSearch(1, newFilters, sortBy, sortDir);
    } else if (preset === '7days') {
      const newFilters = { ...filters, date_from: getLast7DaysCT(), date_to: getTodayCT() };
      setFilters(newFilters);
      setPage(1);
      doSearch(1, newFilters, sortBy, sortDir);
    } else if (preset === 'month') {
      const newFilters = { ...filters, date_from: getThisMonthStartCT(), date_to: getTodayCT() };
      setFilters(newFilters);
      setPage(1);
      doSearch(1, newFilters, sortBy, sortDir);
    } else if (preset === 'custom') {
      setFilters(f => ({ ...f, date_from: '', date_to: '' }));
    }
  }

  function handleSearchClick() {
    setPage(1);
    doSearch(1, buildFilters(), sortBy, sortDir);
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams(buildFilters()).toString();
      const response = await fetch(`/api/calls/history/export?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-history-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export');
    }
    setExporting(false);
  }

  const hasActiveFilters = filters.property_name || filters.region || filters.resolution_type || filters.created_by || filters.resolved_by;

  function clearFilters() {
    const cleared = { ...filters, property_name: '', region: '', resolution_type: '', created_by: '', resolved_by: '' };
    setFilters(cleared);
    setPage(1);
    doSearch(1, cleared, sortBy, sortDir);
  }

  const statsCards = stats ? [
    { label: 'Total Calls', value: stats.total || 0, color: '#0f172a' },
    { label: 'Completed', value: stats.completed || 0, color: '#22c55e' },
    { label: 'Cancelled', value: stats.cancelled || 0, color: '#ef4444' },
    { label: 'Avg Duration', value: stats.avg_resolution_minutes != null ? formatDuration(stats.avg_resolution_minutes) : '--', color: '#6d28d9' },
  ] : [];

  return (
    <div className="history-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2>Call History</h2>
        <button type="button" className="btn btn-gold" onClick={handleExport} disabled={exporting || !searched || results.length === 0}>
          {exporting ? 'Exporting...' : 'Export Excel'}
        </button>
      </div>

      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 12, marginBottom: 20 }}>
          {statsCards.map(s => (
            <div key={s.label} style={{
              background: '#fff', borderRadius: 12, padding: '14px 16px',
              border: '1px solid #e2e8f0', textAlign: 'center',
            }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="search-filters">
        <div className="date-preset-row">
          <button type="button" className={`btn btn-sm ${activePreset === 'today' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('today')}>Today</button>
          <button type="button" className={`btn btn-sm ${activePreset === '7days' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('7days')}>Last 7 Days</button>
          <button type="button" className={`btn btn-sm ${activePreset === 'month' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('month')}>This Month</button>
          <button type="button" className={`btn btn-sm ${activePreset === 'custom' ? 'btn-primary' : 'btn-muted'}`}
            onClick={() => handlePreset('custom')}>Custom</button>
        </div>
        {activePreset === 'custom' && (
          <div className="date-range-row">
            <label>From</label>
            <input type="date" value={filters.date_from}
              onChange={e => setFilters({...filters, date_from: e.target.value})} />
            <label>To</label>
            <input type="date" value={filters.date_to}
              onChange={e => setFilters({...filters, date_to: e.target.value})} />
          </div>
        )}
        <div className="filter-row">
          <input placeholder="Property name..." value={filters.property_name}
            onChange={e => setFilters({...filters, property_name: e.target.value})} />
          <select value={filters.region} onChange={e => setFilters({...filters, region: e.target.value})}>
            <option value="">All Regions</option>
            <option>Fort Worth</option><option>Dallas</option><option>Denton</option>
          </select>
          <select value={filters.resolution_type} onChange={e => setFilters({...filters, resolution_type: e.target.value})}>
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="deleted">Deleted</option>
          </select>
          <select value={filters.created_by} onChange={e => setFilters({...filters, created_by: e.target.value})}>
            <option value="">Entered By</option>
            <option value="public">Resident Portal</option>
            {activeUsers.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
          <select value={filters.resolved_by} onChange={e => setFilters({...filters, resolved_by: e.target.value})}>
            <option value="">Resolved By</option>
            {activeUsers.map(u => (
              <option key={u.id} value={u.id}>{u.full_name}</option>
            ))}
          </select>
          <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSearchClick}>
            {loading ? 'Searching...' : 'Search'}
          </button>
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters}
              style={{ fontSize: 11, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {error && (
        <div style={{padding:'12px 16px',background:'#fee',border:'1px solid #c00',borderRadius:'8px',color:'#900',marginBottom:'16px',fontWeight:600}}>
          {error}
        </div>
      )}

      {searched && (
        <div className="history-results">
          <p className="text-muted">
            {pagination
              ? `${pagination.total} result${pagination.total !== 1 ? 's' : ''} \u2014 Page ${pagination.page} of ${pagination.total_pages}`
              : `${results.length} result${results.length !== 1 ? 's' : ''}`
            }
          </p>
          {results.length === 0 ? (
            <div className="empty-state">No resolved calls found matching your criteria.</div>
          ) : (
            <>
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={thStyle} onClick={() => handleSort('id')}>#{sortArrow('id')}</th>
                      <th style={thStyle} onClick={() => handleSort('property_name')}>Property{sortArrow('property_name')}</th>
                      <th style={thStyle} onClick={() => handleSort('region')} className="hide-mobile">Region{sortArrow('region')}</th>
                      <th style={thStyle} onClick={() => handleSort('call_type_name')}>Type{sortArrow('call_type_name')}</th>
                      <th style={thStyle} onClick={() => handleSort('resolution_type')}>Status{sortArrow('resolution_type')}</th>
                      <th className="hide-mobile">Reason</th>
                      <th className="hide-mobile">Entered By</th><th>Resolved By</th>
                      <th style={thStyle} onClick={() => handleSort('created_at')}>Created{sortArrow('created_at')}</th>
                      <th style={thStyle} onClick={() => handleSort('resolved_at')}>Resolved{sortArrow('resolved_at')}</th>
                      <th style={thStyle} onClick={() => handleSort('resolution_time_minutes')}>Duration{sortArrow('resolution_time_minutes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map(c => (
                      <tr key={c.id} onClick={() => navigate(`/calls/${c.id}`)} className="clickable-row">
                        <td>{c.id}</td>
                        <td>{c.property_name || 'Courtesy Tow'}</td>
                        <td className="hide-mobile">{c.region}</td>
                        <td>{c.call_type_name}</td>
                        <td style={{textTransform:'capitalize'}}>{c.is_deleted ? 'Deleted' : (c.resolution_type || '\u2014')}</td>
                        <td className="hide-mobile">{c.resolution_reason_name}</td>
                        <td className="hide-mobile">{c.is_public_submission ? 'Resident Portal' : (c.created_by_name || '\u2014')}</td>
                        <td>{c.resolved_by_name || '\u2014'}</td>
                        <td>{formatDate(c.created_at)}<br/><span className="text-muted" style={{fontSize:'12px'}}>{formatTime(c.created_at)}</span></td>
                        <td>{formatDate(c.resolved_at)}<br/><span className="text-muted" style={{fontSize:'12px'}}>{formatTime(c.resolved_at)}</span></td>
                        <td>{formatDuration(c.resolution_time_minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pagination && pagination.total_pages > 1 && (
                <div className="pagination-controls">
                  <button type="button" className="btn btn-muted btn-sm" onClick={() => doSearch(page - 1, buildFilters(), sortBy, sortDir)} disabled={page <= 1 || loading}>
                    Previous
                  </button>
                  <span className="pagination-info">Page {pagination.page} of {pagination.total_pages}</span>
                  <button type="button" className="btn btn-muted btn-sm" onClick={() => doSearch(page + 1, buildFilters(), sortBy, sortDir)} disabled={page >= pagination.total_pages || loading}>
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
